import { useState, useEffect, useMemo, useCallback } from "react";
import {
  useFetchCityQuery,
  useUpdateCityMutation,
  useDeleteCityMutation,
  useAddCityMutation,
} from "../../../store";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import { useAlert } from "../../../context/AlertContext";
import { FaCity, FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { ThreeDots } from "react-loader-spinner";
import { TextInput } from "../../../Components/atoms";
import PageShell from "../../../Components/shared/PageShell";
import PageHeader from "../../../Components/shared/PageHeader";
import SearchInput from "../../../Components/shared/SearchInput";
import ListSkeleton from "../../../Components/shared/ListSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import { Pagination } from "../../../Components/atoms";
import Modal from "../../../Components/shared/Modal";

const ITEMS_PER_PAGE = 15;
const SKELETON_COLS = [
  { label: "Şehir", skeletonWidth: "w-32" },
  { label: "İşlemler", skeletonWidth: "w-20" },
];

export default function CityPage() {
  const { auth } = useAuth();
  const token = auth.accessToken;
  const { showSuccess, showError } = useAlert();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("parameters.cityCreate");
  const canEdit = hasPermission("parameters.cityEdit");
  const canDelete = hasPermission("parameters.cityDelete");

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [cityName, setCityName] = useState("");

  const CityResult = useFetchCityQuery(token);
  const [Update, resultUpdate] = useUpdateCityMutation();
  const [Delete, resultDelete] = useDeleteCityMutation();
  const [Add, resultAdd] = useAddCityMutation();

  const cities = useMemo(
    () => CityResult?.data?.data?.cities || [],
    [CityResult?.data?.data?.cities]
  );

  const filteredCities = useMemo(() => {
    if (!searchTerm) return cities;
    const term = searchTerm.toLowerCase();
    return cities.filter((c) => c.cityName?.toLowerCase().includes(term));
  }, [cities, searchTerm]);

  const paginatedCities = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredCities.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredCities, currentPage]);

  const totalPages = Math.ceil(filteredCities.length / ITEMS_PER_PAGE);

  useEffect(() => setCurrentPage(1), [searchTerm]);

  const handleApiResult = useCallback(
    (result, successMsg) => {
      if (result.isSuccess) {
        showSuccess(successMsg);
        setShowModal(false);
        setShowDeleteModal(false);
        setSelectedCity(null);
        setCityName("");
      }
      if (result.isError) {
        showError(result.error?.data?.message || "İşlem başarısız!");
      }
    },
    [showSuccess, showError]
  );

  useEffect(
    () => handleApiResult(resultAdd, "Şehir eklendi!"),
    [resultAdd.isSuccess, resultAdd.isError, handleApiResult]
  );
  useEffect(
    () => handleApiResult(resultUpdate, "Şehir güncellendi!"),
    [resultUpdate.isSuccess, resultUpdate.isError, handleApiResult]
  );
  useEffect(
    () => handleApiResult(resultDelete, "Şehir silindi!"),
    [resultDelete.isSuccess, resultDelete.isError, handleApiResult]
  );

  const openAddModal = () => {
    setSelectedCity(null);
    setCityName("");
    setShowModal(true);
  };

  const openEditModal = (city) => {
    setSelectedCity(city);
    setCityName(city.cityName);
    setShowModal(true);
  };

  const openDeleteModal = (city) => {
    setSelectedCity(city);
    setShowDeleteModal(true);
  };

  const handleSave = () => {
    if (!cityName.trim()) return;
    if (selectedCity) {
      Update({ id: selectedCity._id, cityName, token });
    } else {
      Add({ cityName, token });
    }
  };

  const handleDelete = () => {
    Delete({ id: selectedCity._id, token });
  };

  const isLoading = CityResult.isFetching;

  return (
    <PageShell>
      <div className="p-3 sm:p-4 border-b border-gray-100 flex flex-col gap-3">
        <PageHeader
          icon={FaCity}
          iconColor="blue"
          title="Şehir Yönetimi"
          subtitle="Şehirleri görüntüleyin ve yönetin"
          stats={[
            { label: "Toplam", value: cities.length, color: "blue" },
          ]}
          actions={canCreate ? [
            {
              label: "Yeni Şehir",
              icon: FaPlus,
              onClick: openAddModal,
              variant: "primary",
            },
          ] : []}
          isLoading={isLoading}
        />

        <div className="flex items-center gap-2">
          <SearchInput
            placeholder="Şehir ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            wrapperClassName="flex-1 max-w-sm"
          />
          {canCreate && (
            <button
              onClick={openAddModal}
              className="sm:hidden p-2.5 bg-blue-600 text-white rounded-xl shadow-sm"
            >
              <FaPlus className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {isLoading ? (
          <ListSkeleton columns={SKELETON_COLS} rowCount={10} />
        ) : filteredCities.length === 0 ? (
          <EmptyState
            icon={FaCity}
            message="Şehir bulunamadı"
            suggestion={
              searchTerm
                ? "Farklı bir arama terimi deneyin."
                : "Henüz şehir eklenmemiş."
            }
          />
        ) : (
          <>
            <div className="md:hidden p-3 space-y-2">
              {paginatedCities.map((city) => (
                <div
                  key={city._id}
                  className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between"
                >
                  <span className="text-sm font-medium text-gray-900">
                    {city.cityName}
                  </span>
                  {(canEdit || canDelete) && (
                    <div className="flex gap-1">
                      {canEdit && (
                        <button
                          onClick={() => openEditModal(city)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => openDeleteModal(city)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="hidden md:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Şehir
                    </th>
                    {(canEdit || canDelete) && (
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-32">
                        İşlemler
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedCities.map((city) => (
                    <tr key={city._id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-gray-900">
                        {city.cityName}
                      </td>
                      {(canEdit || canDelete) && (
                        <td className="px-6 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {canEdit && (
                              <button
                                onClick={() => openEditModal(city)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => openDeleteModal(city)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {!isLoading && filteredCities.length > ITEMS_PER_PAGE && (
        <div className="border-t border-gray-100 p-3">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            totalItems={filteredCities.length}
            showInfo
          />
        </div>
      )}

      {/* Add / Edit Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} maxWidth="sm">
        <Modal.Header
          title={selectedCity ? "Şehir Düzenle" : "Yeni Şehir Ekle"}
          onClose={() => setShowModal(false)}
        />
        <Modal.Body>
          <TextInput
            label="Şehir Adı"
            value={cityName}
            onChange={(e) => setCityName(e.target.value)}
            placeholder="Şehir adını girin"
            required
          />
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-3">
          <button
            onClick={() => setShowModal(false)}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            İptal
          </button>
          <button
            onClick={handleSave}
            disabled={resultAdd.isLoading || resultUpdate.isLoading}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {resultAdd.isLoading || resultUpdate.isLoading ? (
              <ThreeDots height={16} width={16} color="white" />
            ) : selectedCity ? (
              "Güncelle"
            ) : (
              "Ekle"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        maxWidth="sm"
      >
        <Modal.Header title="Şehir Sil" onClose={() => setShowDeleteModal(false)} />
        <Modal.Body>
          <p className="text-gray-600">
            <strong>{selectedCity?.cityName}</strong> şehrini silmek
            istediğinize emin misiniz?
          </p>
          <p className="text-sm text-red-600 mt-2">Bu işlem geri alınamaz!</p>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-3">
          <button
            onClick={() => setShowDeleteModal(false)}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            İptal
          </button>
          <button
            onClick={handleDelete}
            disabled={resultDelete.isLoading}
            className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            {resultDelete.isLoading ? (
              <ThreeDots height={16} width={16} color="white" />
            ) : (
              "Sil"
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </PageShell>
  );
}
